/* eslint-disable react/prop-types */
import React from 'react';
import { Field } from 'redux-form';
import FormFieldSearch from '../../../components/forms/FormFieldSearch';
import FormFieldSwitch from '../../../components/forms/FormFieldSwitch';
import FormFieldSelect from '../../../components/forms/FormFieldSelect';

/**
 * Common Mail Recipients Fields Component
 * Used in both ManageEmailReports (with redux-form) and Custom Reports Mail Setup
 *
 * Usage with redux-form (ManageEmailReports):
 * <MailRecipientsFields member="emailReports[0]" />
 *
 * Usage without redux-form (Custom Reports):
 * <MailRecipientsFields
 *   recipientTarget={currentReport.recipient?.target}
 *   recipientCcTarget={currentReport.recipient?.ccTarget}
 *   recipientBccTarget={currentReport.recipient?.bccTarget}
 *   onRecipientTargetChange={(val) => updateRecipient('target', val)}
 *   onRecipientCcTargetChange={(val) => updateRecipient('ccTarget', val)}
 *   onRecipientBccTargetChange={(val) => updateRecipient('bccTarget', val)}
 * />
 *
 * @param {Object} props
 * @param {string} props.member - Redux-form member path (e.g., "emailReports[0]") - if provided, uses Field components
 * @param {Array} props.recipientTarget - Email recipients array - used when not using redux-form
 * @param {Array} props.recipientCcTarget - CC recipients array - used when not using redux-form
 * @param {Array} props.recipientBccTarget - BCC recipients array - used when not using redux-form
 * @param {Function} props.onRecipientTargetChange - Callback for recipient target change - used when not using redux-form
 * @param {Function} props.onRecipientCcTargetChange - Callback for CC recipient change - used when not using redux-form
 * @param {Function} props.onRecipientBccTargetChange - Callback for BCC recipient change - used when not using redux-form
 * @param {string} props.recipientType - 'manual' | 'assignee' | 'variable'
 * @param {Function} props.onRecipientTypeChange - Callback when recipient type changes
 * @param {Array} props.variableOptions - Options for variable selection (id/label), typically email variables
 */
const MailRecipientsFields = ({
  member,
  recipientTarget = [],
  recipientCcTarget = [],
  recipientBccTarget = [],
  onRecipientTargetChange,
  onRecipientCcTargetChange,
  onRecipientBccTargetChange,
  recipientType = 'assignee',
  variableOptions = [],
  onRecipientTypeChange,
  showCc = true,
  showBcc = false,
  showRecipientType = false,
}) => {
  // Determine if we're using redux-form (member is provided)
  const useReduxForm = !!member;

  // Render field using redux-form Field or regular component
  const renderField = (fieldName, componentOrProps, maybeProps) => {
    const component = maybeProps ? componentOrProps : FormFieldSearch;
    const props = maybeProps || componentOrProps;

    if (useReduxForm) {
      return (
        <Field
          component={component}
          name={`${member}.${fieldName}`}
          {...props}
        />
      );
    }

    return React.createElement(component, props);
  };

  // Determine if variable option should be available
  const hasVariableOptions = variableOptions && variableOptions.length > 0;
  // Default to 'assignee' if recipientType is not set
  const effectiveRecipientType = recipientType || 'assignee';

  // Build options list - always include 'manual' and 'assignee', conditionally include 'variable'
  const recipientTypeOptions = [
    // { value: 'manual', label: 'Manual email address' },
    { value: 'assignee', label: 'Assignees' },
  ];
  if (hasVariableOptions) {
    recipientTypeOptions.push({
      value: 'variable',
      label: 'Variable',
    });
  }

  return (
    <>
      {showRecipientType &&
        renderField(
          'recipientType',
          FormFieldSelect,
          useReduxForm
            ? {
                label: 'Recipient Type *',
                options: recipientTypeOptions,
                required: true,
                allowClear: false,
              }
            : {
                label: 'Recipient Type *',
                options: recipientTypeOptions,
                required: true,
                allowClear: false,
                input: {
                  value: effectiveRecipientType,
                  onChange: onRecipientTypeChange,
                },
                meta: { submitFailed: false, error: null },
              },
        )}

      {/* Recipients: manual or variable (assignee hides input). For variable, work like the Variables field. */}
      {effectiveRecipientType !== 'assignee' &&
        renderField('recipient.target', {
          'data-testid': useReduxForm ? 'field-emails' : undefined,
          label: 'Email Recipients',
          mode: effectiveRecipientType === 'variable' ? undefined : 'tags',
          allowClear: effectiveRecipientType === 'variable',
          options:
            effectiveRecipientType === 'variable' ? variableOptions : undefined,
          showSearch: effectiveRecipientType === 'variable',
          placeholder:
            effectiveRecipientType === 'variable'
              ? 'Select email variable'
              : 'Enter email addresses',
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value:
                    effectiveRecipientType === 'variable'
                      ? Array.isArray(recipientTarget)
                        ? recipientTarget[0] || undefined
                        : recipientTarget || undefined
                      : recipientTarget || [],
                  onChange: onRecipientTargetChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {/* CC Recipients: optional manual text input */}
      {showCc &&
        renderField('recipient.ccTarget', {
          'data-testid': useReduxForm ? 'field-cc-emails' : undefined,
          label: 'Email CC Recipients',
          mode: 'tags',
          placeholder: 'Enter CC email addresses',
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: recipientCcTarget || [],
                  onChange: onRecipientCcTargetChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {/* BCC Recipients: optional manual text input */}
      {showBcc &&
        renderField('recipient.bccTarget', {
          'data-testid': useReduxForm ? 'field-bcc-emails' : undefined,
          label: 'Email BCC Recipients',
          mode: 'tags',
          placeholder: 'Enter BCC email addresses',
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: recipientBccTarget || [],
                  onChange: onRecipientBccTargetChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}
    </>
  );
};

export default MailRecipientsFields;
